const MERMAID_SCRIPT_ID = 'lens-mermaid-runtime';
const MERMAID_SCRIPT_SRC = `${process.env.PUBLIC_URL || ''}/lib/mermaid/mermaid.min.js`;

let loadPromise = null;
let initialized = false;
let renderQueue = Promise.resolve();
let renderSeq = 0;

/**
 * Mermaid emits width="100%" + viewBox. In a shrink-wrapped overlay that
 * collapses to an empty white box — pin explicit px size from viewBox.
 */
export function prepareMermaidSvgForViewer(svgHtml) {
    let out = String(svgHtml || '').trim();
    if (!out) return out;

    let width = 0;
    let height = 0;
    const viewBoxMatch = out.match(/\bviewBox\s*=\s*["']([^"']+)["']/i);
    if (viewBoxMatch) {
        const parts = viewBoxMatch[1].trim().split(/[\s,]+/).map(Number);
        if (parts.length === 4 && parts.every(n => Number.isFinite(n))) {
            width = parts[2];
            height = parts[3];
        }
    }

    if (!width) {
        const maxWidthMatch = out.match(/max-width:\s*([\d.]+)px/i);
        if (maxWidthMatch) width = parseFloat(maxWidthMatch[1]);
    }

    const roundW = width > 0 ? Math.round(width) : 0;
    const roundH = height > 0 ? Math.round(height) : 0;
    if (!roundW && !roundH) return out;

    if (roundW) {
        if (/\swidth\s*=\s*["'][^"']*["']/i.test(out)) {
            out = out.replace(/\swidth\s*=\s*["'][^"']*["']/i, ` width="${roundW}"`);
        } else {
            out = out.replace(/<svg\b/i, `<svg width="${roundW}"`);
        }
    }
    if (roundH) {
        if (/\sheight\s*=\s*["'][^"']*["']/i.test(out)) {
            out = out.replace(/\sheight\s*=\s*["'][^"']*["']/i, ` height="${roundH}"`);
        } else {
            out = out.replace(/<svg\b/i, `<svg height="${roundH}"`);
        }
    }

    const styleBits = ['max-width: none'];
    if (roundW) styleBits.push(`width: ${roundW}px`);
    if (roundH) styleBits.push(`height: ${roundH}px`);
    const styleValue = styleBits.join('; ');

    if (/\sstyle\s*=\s*["'][^"']*["']/i.test(out)) {
        out = out.replace(/\sstyle\s*=\s*["'][^"']*["']/i, ` style="${styleValue}"`);
    } else {
        out = out.replace(/<svg\b/i, `<svg style="${styleValue}"`);
    }

    return out;
}

/**
 * Mermaid 10 often resolves with an error SVG (bomb icon) instead of throwing.
 * Successful diagrams still embed `.error-icon` / `.error-text` in their stylesheet,
 * so those class names in the raw string are not an error signal.
 */
export function isMermaidErrorSvg(svg) {
    const raw = String(svg || '');
    if (!raw.trim()) return true;

    if (typeof DOMParser !== 'undefined') {
        try {
            const doc = new DOMParser().parseFromString(raw, 'image/svg+xml');
            const root = doc.documentElement;
            if (!root || root.nodeName.toLowerCase() === 'parsererror') return true;
            const role = (root.getAttribute('aria-roledescription') || '').toLowerCase();
            if (role && role !== 'error') return false;
            if (role === 'error') return true;
            if (typeof root.querySelector === 'function' && root.querySelector('.error-icon, .error-text')) {
                return true;
            }
            const content = (root.textContent || '').toLowerCase();
            if (content.includes('syntax error in text') || content.includes('error rendering')) {
                return true;
            }
        } catch {
            // Fall through to conservative string checks.
        }
    }

    const text = raw.toLowerCase();
    return (
        /aria-roledescription\s*=\s*(['"])error\1/.test(text) ||
        text.includes('syntax error in text')
    );
}

/** Strip characters Mermaid 10 treats as shape/comment syntax inside labels. */
export function mermaidSafeLabel(text, maxLen = 96) {
    let label = String(text || '')
        .replace(/…/g, '...')
        .replace(/[\n\r]/g, ' ');
    label = label.replace(/[()[\]{}]/g, '');
    // `/` is mermaid markdown italic; `&` / quotes break unquoted labels.
    label = label.replace(/["'#;<>|&\\/]/g, ' ');
    label = label.replace(/\s+/g, ' ').trim() || 'unnamed';
    if (label.length > maxLen) {
        label = `${label.slice(0, maxLen - 3).trimEnd()}...`;
    }
    return label;
}

/**
 * LLM flowcharts often use id[Label with (parens) & quotes].
 * Rewrite every [] node label to a quoted sanitized form mermaid 10 can parse.
 */
export function sanitizeMermaidSource(source) {
    let out = String(source || '').replace(
        /(\b[A-Za-z][\w-]*\s*)\[([^\]]*)\]/g,
        (_, id, inner) => {
            const raw = String(inner || '').trim().replace(/^["']|["']$/g, '');
            return `${id}["${mermaidSafeLabel(raw)}"]`;
        }
    );
    // LLM diagrams often use open links (`---`) which 10.9.3 can reject.
    out = out.replace(/(\b[A-Za-z][\w-]*)\s+---\s+(\b[A-Za-z][\w-]*)/g, '$1 --> $2');
    if (/^\s*graph\s/i.test(out)) {
        out = out.replace(/^\s*graph\s/i, 'flowchart ');
    }
    return out;
}

function cleanupMermaidScratch(id) {
    if (typeof document === 'undefined' || !id) return;
    [`${id}`, `d${id}`].forEach(nodeId => {
        const el = document.getElementById(nodeId);
        if (el?.parentNode) el.parentNode.removeChild(el);
    });
}

export function loadMermaid() {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('Mermaid requires a browser environment'));
    }

    if (window.mermaid) {
        return Promise.resolve(window.mermaid);
    }

    if (!loadPromise) {
        loadPromise = new Promise((resolve, reject) => {
            const existing = document.getElementById(MERMAID_SCRIPT_ID);
            if (existing) {
                existing.addEventListener('load', () => {
                    if (window.mermaid) resolve(window.mermaid);
                    else reject(new Error('Mermaid runtime missing after load'));
                });
                existing.addEventListener('error', () => reject(new Error('Mermaid script failed to load')));
                return;
            }

            const script = document.createElement('script');
            script.id = MERMAID_SCRIPT_ID;
            script.src = MERMAID_SCRIPT_SRC;
            script.async = true;
            script.onload = () => {
                if (window.mermaid) resolve(window.mermaid);
                else reject(new Error('Mermaid runtime missing after load'));
            };
            script.onerror = () => reject(new Error('Mermaid script failed to load'));
            document.head.appendChild(script);
        });
    }

    return loadPromise;
}

async function renderMermaidDiagramOnce(id, source, options = {}) {
    const mermaid = await loadMermaid();
    if (!initialized) {
        mermaid.initialize({
            startOnLoad: false,
            // `strict` needs a working DOMPurify.addHook; `loose` is safe after label sanitization.
            securityLevel: 'loose',
            theme: 'neutral',
            fontFamily: 'inherit',
            flowchart: { htmlLabels: false, useMaxWidth: true },
            ...options,
        });
        initialized = true;
    }

    const text = sanitizeMermaidSource(String(source || '').trim());
    if (!text) {
        throw new Error('Empty mermaid diagram');
    }

    const renderId = id || `mermaid-diagram-${++renderSeq}`;
    try {
        const result = await mermaid.render(renderId, text);
        const svg = result?.svg ? String(result.svg) : '';
        if (!svg || isMermaidErrorSvg(svg)) {
            throw new Error('Mermaid syntax error');
        }
        return { ...result, svg };
    } finally {
        cleanupMermaidScratch(renderId);
    }
}

export function renderMermaidDiagram(id, source, options = {}) {
    const pending = renderQueue.then(
        () => renderMermaidDiagramOnce(id, source, options),
        () => renderMermaidDiagramOnce(id, source, options),
    );
    renderQueue = pending.then(
        () => undefined,
        () => undefined,
    );
    return pending;
}

export function resetMermaidLoaderForTests() {
    loadPromise = null;
    initialized = false;
    renderQueue = Promise.resolve();
    renderSeq = 0;
    if (typeof window !== 'undefined') {
        delete window.mermaid;
        const existing = document.getElementById(MERMAID_SCRIPT_ID);
        if (existing) existing.remove();
    }
}
